import React, { useState, useEffect, useRef } from 'react';

const SERVICE_ID  = 'service_jpll3ys';
const TEMPLATE_ID = 'template_xnfkjwn';
const PUBLIC_KEY  = 'SkG-BUEFMjuu8gZEM';

const INITIAL = { name: '', email: '', phone: '', message: '' };

const inputStyle = {
  width: '100%',
  background: '#141414',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 8,
  padding: '0.75rem 1rem',
  fontSize: '0.875rem',
  color: '#f0f0f0',
  outline: 'none',
  transition: 'border-color .2s',
  fontFamily: 'inherit',
};

export default function DemoModal({ open, onClose }) {
  const [form, setForm]     = useState(INITIAL);
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errors, setErrors] = useState({});
  const firstRef            = useRef(null);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    if (open) setTimeout(() => firstRef.current?.focus(), 80);
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    const fn = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose]);

  if (!open) return null;

  function validate() {
    const errs = {};
    if (!form.name.trim())    errs.name    = 'Required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Valid email required';
    if (!form.phone.trim())   errs.phone   = 'Required';
    return errs;
  }

  function change(id, val) {
    setForm(f => ({ ...f, [id]: val }));
    if (errors[id]) setErrors(e => { const n = { ...e }; delete n[id]; return n; });
  }

  function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setStatus('sending');

    fetch('https://api.emailjs.com/api/v1.0/email/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        service_id:  SERVICE_ID,
        template_id: TEMPLATE_ID,
        user_id:     PUBLIC_KEY,
        template_params: {
          name:    form.name,
          email:   form.email,
          phone:   form.phone,
          message: form.message || '—',
        },
      }),
    })
    .then(res => {
      if (!res.ok) throw new Error();
      setStatus('success');
      setForm(INITIAL);
    })
    .catch(() => {
      setStatus('error');
    });
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)', zIndex: 900 }}
      />

      {/* Modal */}
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        zIndex: 901, width: '100%', maxWidth: 460,
        background: '#0e0e0e',
        border: '1px solid rgba(10,158,143,0.18)',
        borderRadius: 16, padding: '2.25rem',
        boxShadow: '0 40px 100px rgba(0,0,0,0.7), 0 0 0 1px rgba(10,158,143,0.06)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>

        {/* Top glow */}
        <div style={{ position: 'absolute', top: -100, left: '50%', transform: 'translateX(-50%)', width: 320, height: 200, background: 'radial-gradient(ellipse, rgba(10,158,143,0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />

        {/* Close */}
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', transition: 'all .2s' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#f0f0f0'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = '#555'; }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* ── Success state ── */}
        {status === 'success' ? (
          <div style={{ textAlign: 'center', padding: '2rem 0', position: 'relative' }}>
            <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(10,158,143,0.1)', border: '1px solid rgba(10,158,143,0.28)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0a9e8f" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h3 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#f0f0f0', marginBottom: 8, letterSpacing: '-0.025em' }}>We'll be in touch.</h3>
            <p style={{ fontSize: '0.875rem', color: '#888', lineHeight: 1.7, marginBottom: '2rem' }}>Thanks for reaching out. Expect to hear from us within one business day.</p>
            <button onClick={onClose} style={{ background: 'rgba(10,158,143,0.1)', border: '1px solid rgba(10,158,143,0.22)', color: '#0a9e8f', padding: '0.6rem 1.75rem', borderRadius: 7, fontSize: '0.875rem', cursor: 'pointer', fontWeight: 500 }}>
              Close
            </button>
          </div>
        ) : (

        /* ── Form state ── */
        <form onSubmit={handleSubmit} noValidate style={{ position: 'relative' }}>

          {/* Header */}
          <div style={{ marginBottom: '1.75rem' }}>
            <div style={{ fontSize: '0.63rem', color: '#077a6e', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.1em', marginBottom: 8 }}>// REQUEST A DEMO</div>
            <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#f0f0f0', letterSpacing: '-0.03em', lineHeight: 1.2, marginBottom: 5 }}>See DialSpark in action.</h2>
            <p style={{ fontSize: '0.78rem', color: '#888', lineHeight: 1.6 }}>Live demo tailored to your team. No slides, no pitch deck.</p>
          </div>

          {/* Fields */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem', marginBottom: '1.25rem' }}>

            {/* Name */}
            <div>
              <label style={{ display: 'block', fontSize: '0.7rem', color: '#909090', marginBottom: 5, fontWeight: 500 }}>Full name *</label>
              <input ref={firstRef} type="text" value={form.name} placeholder="Jane Smith"
                onChange={e => change('name', e.target.value)}
                style={{ ...inputStyle, borderColor: errors.name ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.08)' }}
                onFocus={e => e.target.style.borderColor = errors.name ? 'rgba(239,68,68,0.8)' : 'rgba(10,158,143,0.45)'}
                onBlur={e => e.target.style.borderColor = errors.name ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.08)'}
              />
              {errors.name && <p style={{ fontSize: '0.68rem', color: '#ef4444', marginTop: 3 }}>{errors.name}</p>}
            </div>

            {/* Email */}
            <div>
              <label style={{ display: 'block', fontSize: '0.7rem', color: '#909090', marginBottom: 5, fontWeight: 500 }}>Work email *</label>
              <input type="email" value={form.email} placeholder="jane@company.com"
                onChange={e => change('email', e.target.value)}
                style={{ ...inputStyle, borderColor: errors.email ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.08)' }}
                onFocus={e => e.target.style.borderColor = errors.email ? 'rgba(239,68,68,0.8)' : 'rgba(10,158,143,0.45)'}
                onBlur={e => e.target.style.borderColor = errors.email ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.08)'}
              />
              {errors.email && <p style={{ fontSize: '0.68rem', color: '#ef4444', marginTop: 3 }}>{errors.email}</p>}
            </div>

            {/* Phone */}
            <div>
              <label style={{ display: 'block', fontSize: '0.7rem', color: '#909090', marginBottom: 5, fontWeight: 500 }}>Phone *</label>
              <input type="tel" value={form.phone} placeholder="+44 7700 900000"
                onChange={e => change('phone', e.target.value)}
                style={{ ...inputStyle, borderColor: errors.phone ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.08)' }}
                onFocus={e => e.target.style.borderColor = errors.phone ? 'rgba(239,68,68,0.8)' : 'rgba(10,158,143,0.45)'}
                onBlur={e => e.target.style.borderColor = errors.phone ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.08)'}
              />
              {errors.phone && <p style={{ fontSize: '0.68rem', color: '#ef4444', marginTop: 3 }}>{errors.phone}</p>}
            </div>

            {/* Message */}
            <div>
              <label style={{ display: 'block', fontSize: '0.7rem', color: '#909090', marginBottom: 5, fontWeight: 500 }}>Message <span style={{ color: '#3a3a3a' }}>(optional)</span></label>
              <textarea value={form.message} placeholder="Tell us about your team size, channels, or specific requirements…"
                rows={3}
                onChange={e => change('message', e.target.value)}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 80, lineHeight: 1.6 }}
                onFocus={e => e.target.style.borderColor = 'rgba(10,158,143,0.45)'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
              />
            </div>
          </div>

          {/* Error banner */}
          {status === 'error' && (
            <div style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.18)', borderRadius: 7, padding: '0.6rem 1rem', fontSize: '0.78rem', color: '#ef4444', marginBottom: '1rem' }}>
              Something went wrong — please try again or email us directly.
            </div>
          )}

          {/* Submit */}
          <button type="submit" disabled={status === 'sending'}
            style={{ width: '100%', background: status === 'sending' ? '#077a6e' : '#0a9e8f', color: '#060606', padding: '0.85rem', borderRadius: 8, fontSize: '0.9375rem', fontWeight: 700, border: 'none', cursor: status === 'sending' ? 'default' : 'pointer', transition: 'all .2s', letterSpacing: '-0.01em' }}
            onMouseEnter={e => { if (status !== 'sending') { e.currentTarget.style.background = '#0db8a7'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(10,158,143,0.25)'; }}}
            onMouseLeave={e => { e.currentTarget.style.background = status === 'sending' ? '#077a6e' : '#0a9e8f'; e.currentTarget.style.boxShadow = 'none'; }}>
            {status === 'sending' ? 'Sending…' : 'Book my demo →'}
          </button>

          <p style={{ textAlign: 'center', fontSize: '0.68rem', color: '#2e2e2e', marginTop: '0.9rem' }}>No spam. We respect your privacy.</p>
        </form>
        )}
      </div>
    </>
  );
}
